import { useState, ChangeEvent, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ButtonSiguiente, Input } from "@/components/ui";
import { safeFetch } from "@/api/safeFetch";
import Alerta from "@/components/alert/Alerta";
import { AfiliadoCodigo, AlertaType } from "@/types";
import { PopupEditarAfiliado } from "@/components/popup";

export default function EditarAfiliados() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const PAIS_PREDETERMINADO = "ESPAÑA";

  // ======================
  // Estados buscador
  // ======================
  const [busqueda, setBusqueda] = useState("");
  const [loadingBuscar, setLoadingBuscar] = useState(false);
  const [error, setError] = useState("");

  // ======================
  // Estados formulario
  // ======================
  const initialFormState = {
    nombre: "",
    apellidos: "",
    correo_electronico: "",
    numero_telefono: "",
    dni: "",
    codigo: "",
    puntos: 0,
    puntos_por_suscriptor: 0,
    porcentaje_descuento: 0,
    fecha_expiracion: "",
    direccion: {
      calle: "",
      localidad: "",
      provincia: "",
      codigo_postal: "",
      pais: PAIS_PREDETERMINADO,
    },
  };

  const [form, setForm] = useState(initialFormState);
  const [afiliadoId, setAfiliadoId] = useState<string | null>(null);
  const [loadingGuardar, setLoadingGuardar] = useState(false);
  const [errores, setErrores] = useState<{ [key: string]: boolean }>({});
  const [alerta, setAlerta] = useState<{
    mensaje: string;
    type: AlertaType;
  } | null>(null);

  // ======================
  // Popup
  // ======================
  const [mostrarPopup, setMostrarPopup] = useState(false);

  // ======================
  // Handlers
  // ======================
  const handleBuscar = async () => {
    if (!busqueda.trim()) return;

    const controller = new AbortController();
    const signal = controller.signal;

    setLoadingBuscar(true);
    setError("");
    setAfiliadoId(null);

    try {
      const res = await safeFetch(
        `/api/afiliados/buscar?query=${encodeURIComponent(busqueda.trim())}`,
        { headers: { Authorization: `Bearer ${token}` }, signal },
      );

      if (!res.ok) throw new Error("Afiliado no encontrado");

      const data: AfiliadoCodigo = await res.json();

      setForm({
        nombre: data.nombre,
        apellidos: data.apellidos,
        correo_electronico: data.correo_electronico,
        numero_telefono: data.numero_telefono,
        dni: data.dni,
        codigo: data.codigo,
        puntos: data.puntos,
        puntos_por_suscriptor: data.puntos_por_suscriptor,
        porcentaje_descuento: data.porcentaje_descuento,
        fecha_expiracion: data.fecha_expiracion?.slice(0, 10),
        direccion: {
          ...data.direccion,
          pais: data.direccion?.pais || PAIS_PREDETERMINADO,
        },
      });

      setAfiliadoId(data.id);
    } catch (err: any) {
      if (err.name !== "AbortError") {
        setError(err.message || "Error buscando afiliado");
      }
    } finally {
      setLoadingBuscar(false);
    }

    return () => controller.abort();
  };

  const NO_UPPER = ["correo_electronico"];

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value, type } = e.target;

    // Campos numéricos
    if (type === "number") {
      const numericValue = value === "" ? 0 : Number(value);

      setForm((prev) => ({
        ...prev,
        [name]: numericValue,
      }));

      return;
    }

    const finalValue = NO_UPPER.includes(name) ? value : value.toUpperCase();

    if (name.startsWith("direccion.")) {
      const key = name.split(".")[1];

      setForm((prev) => ({
        ...prev,
        direccion: {
          ...prev.direccion,
          [key]: finalValue,
        },
      }));
    } else {
      setForm((prev) => ({
        ...prev,
        [name]: finalValue,
      }));
    }
  };

  const validarFormulario = () => {
    const nuevosErrores: { [key: string]: boolean } = {};
    const campos = [
      "nombre",
      "apellidos",
      "correo_electronico",
      "numero_telefono",
      "porcentaje_descuento",
      "fecha_expiracion",
      "direccion.calle",
      "direccion.localidad",
      "direccion.provincia",
      "direccion.codigo_postal",
      "direccion.pais",
    ];

    campos.forEach((field) => {
      const value = field.includes(".")
        ? field.split(".").reduce((acc: any, key: string) => acc[key], form)
        : form[field as keyof typeof form];
      if (!value || (typeof value === "string" && value.trim() === "")) {
        nuevosErrores[field] = true;
      }
    });

    setErrores(nuevosErrores);
    return Object.keys(nuevosErrores).length === 0;
  };

  // ======================
  // Confirmación de edición (popup)
  // ======================
  const confirmarEdicion = async () => {
    if (!afiliadoId) return;
    if (!validarFormulario()) {
      setAlerta({
        mensaje: "Por favor rellena todos los campos obligatorios.",
        type: "error",
      });
      return;
    }

    setLoadingGuardar(true);
    try {
      const res = await safeFetch(`/api/afiliados/editar/${afiliadoId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(form),
      });

      if (!res.ok) throw new Error("Error guardando cambios");

      setAlerta({
        mensaje: "Afiliado actualizado correctamente ✅",
        type: "success",
      });
    } catch (err) {
      setAlerta({
        mensaje: err instanceof Error ? err.message : "Error guardando",
        type: "error",
      });
    } finally {
      setLoadingGuardar(false);
      setMostrarPopup(false);
    }
  };

  useEffect(() => {
    return () => {
      setBusqueda("");
      setAfiliadoId(null);
      setForm(initialFormState);
      setError("");
      setLoadingBuscar(false);
      setLoadingGuardar(false);
      setErrores({});
      setAlerta(null);
      setMostrarPopup(false);
    };
  }, []);

  // ======================
  // Render
  // ======================
  return (
    <div className="w-full min-h-screen flex items-center justify-center bg-zinc-500 p-4 relative">
      {/* 🔙 Botón volver arriba izquierda */}
      <div className="absolute top-4 left-4">
        <ButtonSiguiente
          onClick={() => navigate("/afiliados")}
          className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 transition shadow"
        >
          ⬅ Volver
        </ButtonSiguiente>
      </div>

      <div className="w-full max-w-2xl bg-zinc-400 p-4 sm:p-6 rounded-xl shadow-md border-zinc-600 border-r-4 border-t-2 shadow-yellow-500 flex flex-col gap-6">
        <h2 className="text-xl sm:text-2xl text-center font-bold">
          Editar Afiliado
        </h2>

        {/* BUSCADOR */}
        <div className="w-full bg-yellow-500 p-4 rounded-xl flex flex-col md:flex-row gap-4 items-stretch border-zinc-200 border border-r-4 border-t-2 shadow-md shadow-yellow-500">
          <Input
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar por DNI, código o nombre"
            className="h-10 px-3 rounded w-full md:w-1/2"
          />
          <ButtonSiguiente
            className="!h-9 px-6 bg-green-600 text-white rounded w-full md:w-1/2"
            onClick={handleBuscar}
            disabled={loadingBuscar}
          >
            {loadingBuscar ? "Buscando..." : "Buscar"}
          </ButtonSiguiente>
        </div>

        {error && <p className="text-red-600 text-center">{error}</p>}

        {/* FORMULARIO */}
        {afiliadoId && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setMostrarPopup(true);
            }}
            className="grid grid-cols-12 gap-4"
          >
            <Input
              type="text"
              name="nombre"
              placeholder="Nombre"
              value={form.nombre}
              onChange={handleChange}
              error={errores["nombre"]}
              className="col-span-12 p-2 rounded"
            />
            <Input
              type="text"
              name="apellidos"
              placeholder="Apellidos"
              value={form.apellidos}
              onChange={handleChange}
              error={errores["apellidos"]}
              className="col-span-12 p-2 rounded"
            />
            <Input
              type="text"
              name="dni"
              placeholder="DNI/NIE"
              value={form.dni}
              onChange={handleChange}
              disabled
              className="col-span-12 p-2 rounded uppercase cursor-not-allowed"
            />
            <Input
              type="email"
              name="correo_electronico"
              placeholder="Correo electrónico"
              value={form.correo_electronico}
              onChange={handleChange}
              error={errores["correo_electronico"]}
              className="col-span-12 p-2 rounded"
            />
            <Input
              type="text"
              name="numero_telefono"
              placeholder="Teléfono"
              value={form.numero_telefono}
              onChange={handleChange}
              error={errores["numero_telefono"]}
              className="col-span-12 p-2 rounded uppercase"
            />
            <div className="col-span-12 grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Código */}
              <div className="flex flex-col gap-1">
                <label className="ml-2 text-sm text-gray-600">Código</label>

                <Input
                  type="text"
                  name="codigo"
                  placeholder="Código"
                  value={form.codigo}
                  onChange={handleChange}
                  disabled
                  className="w-full p-2 rounded uppercase cursor-not-allowed"
                />
              </div>

              {/* Puntos acumulados */}
              <div className="flex flex-col gap-1">
                <label className="ml-2 text-sm text-gray-600">
                  Puntos acumulados
                </label>

                <Input
                  type="number"
                  name="puntos"
                  placeholder="Puntos acumulados"
                  min={0}
                  value={form.puntos}
                  onChange={handleChange}
                  className="w-full p-2 rounded"
                />
              </div>

              {/* Puntos por suscriptor */}
              <div className="flex flex-col gap-1">
                <label className="ml-2 text-sm text-gray-600">
                  Ptos. Suscripción
                </label>

                <Input
                  type="number"
                  name="puntos_por_suscriptor"
                  placeholder="Puntos por suscriptor"
                  min={0}
                  value={form.puntos_por_suscriptor}
                  onChange={handleChange}
                  className="w-full p-2 rounded"
                />
              </div>

              {/* Descuento */}
              <div className="flex flex-col gap-1">
                <label className="ml-2 text-sm text-gray-600">
                  % Descuento
                </label>

                <Input
                  type="number"
                  name="porcentaje_descuento"
                  placeholder="% Descuento"
                  min={0}
                  max={100}
                  value={form.porcentaje_descuento}
                  onChange={handleChange}
                  error={errores["porcentaje_descuento"]}
                  className="w-full p-2 rounded"
                />
              </div>
            </div>

            <label className="flex ml-2 -mb-4 text-sm text-gray-600 whitespace-nowrap">
              Fecha de expiración código
            </label>
            <Input
              type="date"
              name="fecha_expiracion"
              value={form.fecha_expiracion}
              onChange={handleChange}
              error={errores["fecha_expiracion"]}
              className="col-span-12 p-2 rounded h-9"
            />
            <Input
              type="text"
              name="direccion.calle"
              placeholder="Calle"
              value={form.direccion.calle}
              onChange={handleChange}
              error={errores["direccion.calle"]}
              className="col-span-12 p-2 rounded uppercase"
            />
            <Input
              type="text"
              name="direccion.localidad"
              placeholder="Localidad"
              value={form.direccion.localidad}
              onChange={handleChange}
              error={errores["direccion.localidad"]}
              className="col-span-6 p-2 rounded uppercase"
            />
            <Input
              type="text"
              name="direccion.provincia"
              placeholder="Provincia"
              value={form.direccion.provincia}
              onChange={handleChange}
              error={errores["direccion.provincia"]}
              className="col-span-6 p-2 rounded uppercase"
            />
            <Input
              type="text"
              name="direccion.codigo_postal"
              placeholder="Código postal"
              value={form.direccion.codigo_postal}
              onChange={handleChange}
              error={errores["direccion.codigo_postal"]}
              className="col-span-6 p-2 rounded uppercase"
            />
            <Input
              type="text"
              name="direccion.pais"
              placeholder="País"
              value={form.direccion.pais}
              onChange={handleChange}
              error={errores["direccion.pais"]}
              className="col-span-6 p-2 rounded uppercase"
            />

            <ButtonSiguiente
              type="submit"
              className="col-span-12 bg-green-600 text-white p-3 rounded hover:bg-green-700 transition"
              disabled={loadingGuardar}
            >
              {loadingGuardar ? "Guardando..." : "Guardar cambios"}
            </ButtonSiguiente>
          </form>
        )}

        {!afiliadoId && (
          <p className="text-center text-gray-700">
            Busca un afiliado para editar
          </p>
        )}
      </div>

      {/* ALERTA */}
      {alerta && (
        <Alerta
          mensaje={alerta.mensaje}
          type={alerta.type}
          onClose={() => setAlerta(null)}
        />
      )}

      {/* POPUP */}
      {mostrarPopup && (
        <PopupEditarAfiliado
          titulo="Guardar cambios"
          descripcion="Se guardarán los cambios del afiliado."
          textoConfirmar="Guardar"
          redirectOnConfirm={false}
          onConfirm={confirmarEdicion} // tu función async que guarda los cambios
          onCancel={() => setMostrarPopup(false)}
        />
      )}
    </div>
  );
}
